<template>
  <div class="gas-state-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$PV = nRT &nbsp;;|&nbsp; P = ⅔nE_k$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          
          <!-- Piston Cylinder Container -->
          <rect x="80" y="30" :width="containerWidth" height="150" fill="rgba(6, 182, 212, 0.05)" stroke="#334155" stroke-width="3" />
          
          <!-- Moving Piston Base -->
          <rect :x="80 + containerWidth" y="20" width="12" height="170" fill="#64748b" rx="2" stroke="#475569" stroke-width="2" />
          <!-- Piston Rod -->
          <rect :x="92 + containerWidth" y="100" width="40" height="10" fill="#475569" />

          <!-- Dynamic Particles (Gas Molecules) -->
          <g>
             <!-- Using trail effect or just colored dots -->
             <circle v-for="p in particles" :key="p.id" :cx="p.x" :cy="p.y" r="3" :fill="particleColor" opacity="0.8" />
          </g>
          
          <!-- Measurement Gauge (Pressure) -->
          <g transform="translate(45, 105)">
             <circle r="30" fill="#1e293b" stroke="#475569" stroke-width="2" />
             <line x1="-20" y1="0" x2="-15" y2="0" stroke="#94a3b8" />
             <line x1="0" y1="-20" x2="0" y2="-15" stroke="#94a3b8" />
             <line x1="20" y1="0" x2="15" y2="0" stroke="#94a3b8" />
             
             <!-- Animated needle based on simulated collision impulse -->
             <line x1="0" y1="0" :x2="needleX" :y2="needleY" stroke="#ef4444" stroke-width="2" stroke-linecap="round" />
             <circle r="3" fill="#cbd5e1" />
             <text y="45" text-anchor="middle" fill="#94a3b8" font-size="9">微观碰撞压强 P</text>
             <text y="-8" text-anchor="middle" fill="#f8fafc" font-size="11" font-weight="bold">{{ measuredPressure.toFixed(1) }}</text>
          </g>

        </svg>

        <div class="hud-panel" style="right: 12px; left: auto;">
           <div class="hud-item cyan" style="text-align: right;">
              <span class="val">{{ volume.toFixed(1) }} <small>L</small></span>
              <label>容器体积 V</label>
           </div>
           <div class="hud-item orange" style="text-align: right;">
              <span class="val">{{ temperature.toFixed(0) }} <small>K</small></span>
              <label>热力学温度 T (v²)</label>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节活塞压缩体积 V</label>
                    <el-slider v-model="volume" :min="20" :max="100" :step="1" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">加热系统温度 T</label>
                    <el-slider v-model="temperature" :min="100" :max="1000" :step="10" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>这是一个真实的分子动力学统计模拟！压强并非直接套用 <code>PV=nRT</code> 的公式作假，而是物理引擎每一帧独立计算大量气体分子撞击容器内壁所产生的由于动量反转 <code>Δp = 2mv_x</code> 累积的平均冲量。温度 <code>T</code> 严格对应分子均方根速度 <code>v ∝ √T</code>。当你剧烈压缩体积 <code>V</code> 时，单位面积上分子撞击器壁的频率急剧上升；当你升高温度时，每次撞击的冲量和频率又同时提升，仪表盘实测的微观冲力完美印证了宏观理想气体方程现象！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IdealGasStateViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      volume: 80, // percentage practically
      temperature: 300,
      particles: [],
      timer: null,
      impulseAccumulator: 0,
      measuredPressure: 0,
      ticks: 0
    }
  },
  computed: {
    containerWidth() { return this.volume * 2.5; },
    particleColor() {
       // Color maps monotonically from low T to high T
       if (this.temperature < 200) return '#38bdf8'; // Cold blue
       if (this.temperature < 400) return '#4ade80'; // Room green
       if (this.temperature < 700) return '#facc15'; // Hot yellow
       return '#ef4444'; // Red hot
    },
    needleX() {
       // Max pressure expected approx 100 for gauge scaling
       let maxP = 150;
       let ratio = this.measuredPressure / maxP;
       if(ratio > 1) ratio = 1;
       let angle = -Math.PI/1.2 + ratio * (1.6 * Math.PI);
       return Math.cos(angle) * 22;
    },
    needleY() {
       let maxP = 150;
       let ratio = this.measuredPressure / maxP;
       if(ratio > 1) ratio = 1;
       let angle = -Math.PI/1.2 + ratio * (1.6 * Math.PI);
       return Math.sin(angle) * 22;
    }
  },
  methods: {
    initParticles() {
       for(let i=0; i<60; i++) {
          // base velocities derived later from T
          let angle = Math.random() * Math.PI * 2;
          this.particles.push({
             id: i,
             x: 80 + Math.random() * (this.containerWidth - 5),
             y: 30 + Math.random() * 140,
             dirX: Math.cos(angle),
             dirY: Math.sin(angle)
          });
       }
    },
    animate() {
       // Root Mean Square velocity v is proportional to sqrt(T)
       let vRMS = Math.sqrt(this.temperature) * 0.2;
       
       const top = 33;
       const bottom = 177;
       const left = 83;
       
       let currentRight = 80 + this.containerWidth - 3; // Accounts for radius

       this.particles.forEach(p => {
          // Physical step
          p.x += p.dirX * vRMS;
          p.y += p.dirY * vRMS;

          // Wall collisions & pressure impulse accumulation
          // Impulse = 2 * m * v_perp. We assume m=1, so dP = 2 * v_perp
          
          if (p.x < left) {
             p.x = left; p.dirX *= -1;
             this.impulseAccumulator += 2 * Math.abs(p.dirX * vRMS);
          } else if (p.x > currentRight) {
             p.x = currentRight; p.dirX *= -1;
             this.impulseAccumulator += 2 * Math.abs(p.dirX * vRMS);
          }
          
          if (p.y < top) {
             p.y = top; p.dirY *= -1;
             // Top and bottom walls also contribute to global pressure measurement
             this.impulseAccumulator += 2 * Math.abs(p.dirY * vRMS);
          } else if (p.y > bottom) {
             p.y = bottom; p.dirY *= -1;
             this.impulseAccumulator += 2 * Math.abs(p.dirY * vRMS);
          }
       });

       this.ticks++;
       // Calculate pressure every 10 frames based on surface area and impulse
       if (this.ticks % 10 === 0) {
          // Pressure = Force / Area. Force = Impulse / Time.
          // Area mapping: 2*(Width + Height)
          let totalArea = 2 * (this.containerWidth + 150);
          let force = this.impulseAccumulator / 10; 
          let calcP = (force / totalArea) * 1000; // scaling factor for view
          
          // Smooth the pressure output
          this.measuredPressure = this.measuredPressure * 0.7 + calcP * 0.3;
          this.impulseAccumulator = 0;
       }

       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.initParticles();
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.gas-state-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-right: 3px solid #06b6d4; } &.orange { border-right: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
</style>
